import { useEffect, useState } from 'react'
import { useFetcher } from '@remix-run/react'
import reCAPTCHA from './useRecaptcha'
import { formErrors } from '~/types'
import { getGlobal } from '~/utils'
import { action } from '~/routes/api.form-submit'
import { useLocation } from '@remix-run/react'

type fieldsType = {
  firstName?: string
  lastName?: string
  email?: string
  phone?: string
  travelAgent?: boolean
  abta?: string
  plans?: string
  downloadFile?: boolean
  newsletterSignup?: boolean
  preOrder?: boolean
  addressLine1?: string
  addressLine2?: string
  city?: string
  postcode?: string
  copies?: string
  formType?: string
}

const useForms = (file?: string) => {
  const fetcher = useFetcher<typeof action>()
  const { search } = useLocation()
  const [activeFormTab, setActiveFormTab] = useState<number>(0)
  const [submitButton, setSubmitButton] = useState<string>('Submit')

  // Form Fields
  const [fields, setFields] = useState<fieldsType>({})

  const handleChange = (name: string, value: string | number | boolean) => {
    setFields({
      ...fields,
      [name]: value,
    })
  }

  // Form errors
  const [errors, setErrors] = useState<formErrors>({})

  // Response
  const [response, setResponse] = useState<{
    message?: string
    type?: 'loading' | 'success' | 'error'
  }>({})

  // recaptcha
  const { env } = getGlobal()
  if (!env.RECAPTCHA_SITE_KEY) throw new Error('No Recaptcha key in env')
  const recaptcha = new reCAPTCHA(env.RECAPTCHA_SITE_KEY, 'form')

  const downloadFile = () => {
    if (!file) return
    const a = document.createElement('a')
    a.download = 'FCC Brochure.pdf'
    a.href = file
    a.target = '_blank'
    a.addEventListener('click', e => {
      setTimeout(() => URL.revokeObjectURL(a.href), 30 * 1000)
    })
    a.click()
  }

  const fieldValidation = (fields: any) => {
    // check for errors with formdata
    setErrors({})
    const newErrors: formErrors = {}
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    const requiredFields = ['firstName', 'lastName', 'email', 'phone', 'plans']

    requiredFields.forEach(field => {
      if (!fields[field]) {
        newErrors[field] = `This field is required`
      }
    })

    if (fields.email && !emailRegex.test(fields.email)) {
      newErrors.email = 'Invalid email format'
    }

    if (fields.travelAgent && !fields.abta) {
      newErrors.abta = 'ABTA is required'
    }

    if (
      fields.preOrder &&
      !(
        fields.addressLine1 ||
        fields.addressLine2 ||
        fields.city ||
        fields.postcode ||
        fields.copies
      )
    ) {
      const addressFields = [
        'addressLine1',
        'addressLine2',
        'city',
        'postcode',
        'copies',
      ]
      addressFields.forEach(field => {
        if (!fields[field]) {
          newErrors[field] = 'This field is required'
        }
      })
    }
    setErrors(newErrors)

    if (Object.keys(newErrors).length >= 1) {
      setResponse({ message: 'Please check your form.', type: 'error' })
      return false
    }

    return true
  }

  const handleSubmit = async (
    fields: any,
    formType: string,
    downloadFile?: boolean
  ) => {
    // Disable submit button
    setSubmitButton('Sending...')

    // Set response to loading
    setResponse({ message: 'Your form is sending.', type: 'loading' })

    // If errors exsist return null and show errors on form
    const valid = fieldValidation(fields)

    const formData = new FormData()

    Object.entries(fields).forEach(([key, value]) => {
      formData.append(key, value as string)
    })

    formData.append('formName', formType)

    if (downloadFile) formData.append('file', 'true')

    if (valid) {
      // captcha here
      const token = await recaptcha.getToken().catch(error => {
        console.log(error)
      })

      if (token) {
        formData.append('captchaToken', token)

        fetcher.submit(formData, {
          method: 'post',
          action: '/api/form-submit',
        })
      } else {
        // Reable submit button
        setSubmitButton('Submit')
      }
    } else {
      // Reable submit button
      setSubmitButton('Submit')
    }
  }

  // On receiving form response
  useEffect(() => {
    if (fetcher.data?.response?.success == true) {
      // download file if needed
      if (fetcher.data?.response?.downloadFile == true) downloadFile()

      // Reable submit button
      setSubmitButton('Submit')

      setResponse({
        message: 'Thank you for getting in touch!',
        type: 'success',
      })

      setTimeout(() => {
        setResponse({})
      }, 3000)

      // Clear all form data
      setFields({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        travelAgent: false,
        abta: '',
        plans: '',
        downloadFile: false,
        newsletterSignup: false,
        preOrder: false,
        addressLine1: '',
        addressLine2: '',
        city: '',
        postcode: '',
        copies: '',
        formType: '',
      })

      return
    } else {
      setResponse({
        message: fetcher.data?.response.message,
        type: 'error',
      })
      return
    }
  }, [fetcher.data?.response])

  // Check to see if link is for download form and show.
  useEffect(() => {
    const tab = search.replace('?tab=', '').replace('&', '')
    if (tab === 'download') {
      setActiveFormTab(1)
    } else {
      setActiveFormTab(0)
    }
  }, [search])

  return {
    errors,
    response,
    handleSubmit,
    activeFormTab,
    fields,
    handleChange,
    submitButton,
  }
}

export default useForms
